import React, { useState, useMemo, useEffect } from 'react';
import {
  Search,
  Brain,
  BookOpen,
  Lightbulb,
  Target,
  Sparkles,
  TrendingUp,
  User,
  MapPin,
  Clock,
  BarChart3,
  X,
  ChevronDown,
  ChevronUp,
  Award,
  MessageSquare,
  FileText,
  Hash,
  Building,
  Heart,
  Users
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const WritingSupportSidebar = ({ 
  currentMode, 
  selectedDocument, 
  assessmentData, 
  personalProfile,
  essays,
  attachedFreewrites: propAttachedFreewrites = [],
  attachedIdeas: propAttachedIdeas = [],
  collegeInsights: propCollegeInsights = null,
  slotId: propSlotId = null,
  onClose,
  className = ""
}) => {
  const [activeTab, setActiveTab] = useState('freewrites');
  const [expandedSections, setExpandedSections] = useState({
    strategic: true,
    context: true,
    connections: false
  });
  const [expandedItems, setExpandedItems] = useState({});
  const [loadedAttachedContent, setLoadedAttachedContent] = useState({
    freewrites: [],
    ideas: [],
    insights: null
  });
  const [slotInfo, setSlotInfo] = useState(null);
  
  // Use props if provided, otherwise use loaded content
  const attachedFreewrites = propAttachedFreewrites.length > 0 ? propAttachedFreewrites : loadedAttachedContent.freewrites;
  const attachedIdeas = propAttachedIdeas.length > 0 ? propAttachedIdeas : loadedAttachedContent.ideas;
  const collegeInsights = propCollegeInsights || loadedAttachedContent.insights;
  
  // Debug logging
  useEffect(() => {
    console.log('WritingSupportSidebar Debug:', {
      selectedDocument,
      slot_ids: selectedDocument?.slot_ids,
      propAttachedFreewrites,
      propAttachedIdeas,
      propCollegeInsights,
      loadedAttachedContent,
      attachedFreewrites,
      attachedIdeas,
      collegeInsights,
      slotInfo
    });
  }, [selectedDocument, loadedAttachedContent, slotInfo]);

  // Load attached content if selectedDocument has slot_ids or if slotId is provided
  useEffect(() => {
    const loadAttachedContentFromSlot = async () => {
      // Check for slot ID from props first, then from document
      const slotId = propSlotId || selectedDocument?.slot_ids?.[0];
      
      if (!slotId) {
        setSlotInfo(null);
        // If no slot but we have an essay, try to find the slot by essay ID
        if (selectedDocument?.id) {
          console.log('No slot_ids on essay, searching for slot by essay ID:', selectedDocument.id);
          const { data: slots } = await supabase
            .from('essay_slots')
            .select('id')
            .eq('assigned_essay_id', selectedDocument.id)
            .limit(1);
          
          if (slots && slots.length > 0) {
            console.log('Found slot for essay:', slots[0].id);
            // Recursively call with the found slot ID
            loadSlotContent(slots[0].id);
            return;
          }
        }
        return;
      }

      loadSlotContent(slotId);
    };

    const loadSlotContent = async (slotId) => {
      try {
        console.log('Loading slot content for slot ID:', slotId);
        
        // Load the slot data with college information
        const { data: slot, error: slotError } = await supabase
          .from('essay_slots')
          .select(`
            *,
            college:college_journey_colleges(
              name,
              category
            )
          `)
          .eq('id', slotId)
          .single();

        if (slotError) {
          console.error('Error loading slot:', slotError);
          return;
        }

        console.log('Loaded slot data:', slot);
        
        // Store slot info for display
        setSlotInfo({
          type: slot.slot_type,
          prompt: slot.prompt_text,
          wordLimit: slot.word_limit,
          collegeName: slot.college?.name,
          isOptional: slot.is_optional
        });

        // Load attached freewrites
        let freewrites = [];
        if (slot.assigned_freewrite_ids && slot.assigned_freewrite_ids.length > 0) {
          console.log('Loading freewrites:', slot.assigned_freewrite_ids);
          const { data: freewriteData, error: fwError } = await supabase
            .from('college_journey_freewrites')
            .select('*')
            .in('id', slot.assigned_freewrite_ids)
            .order('created_at', { ascending: false });
          
          if (fwError) {
            console.error('Error loading freewrites:', fwError);
          } else if (freewriteData) {
            freewrites = freewriteData;
            console.log('Loaded freewrites:', freewrites);
          }
        }

        // Load attached ideas
        let ideas = [];
        if (slot.assigned_idea_ids && slot.assigned_idea_ids.length > 0) {
          console.log('Loading ideas:', slot.assigned_idea_ids);
          const { data: ideaData, error: ideaError } = await supabase
            .from('essay_ideas')
            .select('*')
            .in('id', slot.assigned_idea_ids)
            .order('created_at', { ascending: false });
          
          if (ideaError) {
            console.error('Error loading ideas:', ideaError);
          } else if (ideaData) {
            ideas = ideaData;
            console.log('Loaded ideas:', ideas);
          }
        }

        // Set the loaded content
        setLoadedAttachedContent({
          freewrites,
          ideas,
          insights: slot.college_insights || null
        });
      } catch (error) {
        console.error('Error loading attached content:', error);
      }
    };

    // Load content when selectedDocument changes
    loadAttachedContentFromSlot();
  }, [selectedDocument?.id, selectedDocument?.slot_ids, propSlotId]);

  // Handle Escape key to close panel
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };

    if (onClose) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [onClose]);

  // Process assessment insights
  const rawInsights = assessmentData?.formatted_insights || assessmentData?.insights;
  const insights = rawInsights?.insights || rawInsights || {};

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleItem = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Get context-specific content based on current mode
  const getContextContent = () => {
    switch (currentMode) {
      case 'FREEWRITE':
        return (
          <div className="space-y-4">
            <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-azure-600" />
                <h4 className="font-medium text-azure-900">Free Writing Tips</h4>
              </div>
              <ul className="text-sm text-azure-800 space-y-1">
                <li>• Write continuously without stopping</li>
                <li>• Don't worry about grammar or spelling</li>
                <li>• Let thoughts flow naturally</li>
                <li>• Use prompts if you get stuck</li>
              </ul>
            </div>
          </div>
        );

      case 'ESSAYS':
        return (
          <div className="space-y-4">
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-violet-600" />
                <h4 className="font-medium text-violet-900">Essay Strategy</h4>
              </div>
              <ul className="text-sm text-violet-800 space-y-1">
                <li>• Show, don't tell through specific examples</li>
                <li>• Connect experiences to future goals</li>
                <li>• Be authentic and reflective</li>
                <li>• Use active voice and strong verbs</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Get strategic insights from assessment
  const getStrategicInsights = () => {
    if (!insights) return [];
    
    const strategicItems = [];
    
    // Add strategic positioning insights
    if (insights.strategicPositioning) {
      strategicItems.push({
        icon: <Target className="w-4 h-4 text-emerald-600" />,
        title: "Your Strategic Brand",
        content: insights.strategicPositioning.brand || insights.strategicPositioning.whyItWorks,
        type: "brand"
      });
    }

    // Add tactical advantages
    if (insights.tacticalAdvantages) {
      const advantages = Object.values(insights.tacticalAdvantages).filter(Boolean);
      if (advantages.length > 0) {
        strategicItems.push({
          icon: <Sparkles className="w-4 h-4 text-violet-600" />,
          title: "Tactical Advantages",
          content: advantages.join(" • "),
          type: "advantages"
        });
      }
    }

    // Add hidden superpowers
    if (insights.hiddenSuperpowers?.superpowers) {
      const superpowers = Array.isArray(insights.hiddenSuperpowers.superpowers) 
        ? insights.hiddenSuperpowers.superpowers 
        : [insights.hiddenSuperpowers.superpowers];
      
      strategicItems.push({
        icon: <Brain className="w-4 h-4 text-azure-600" />,
        title: "Hidden Superpowers",
        content: superpowers.slice(0, 3).join(" • "),
        type: "superpowers"
      });
    }

    return strategicItems;
  };

  // Get thematic connections between essays and free writes
  const getThematicConnections = () => {
    if (!selectedDocument || !essays) return [];
    
    const connections = [];
    
    // Simple keyword-based connections (could be enhanced with AI)
    const currentText = selectedDocument.content || selectedDocument.body || "";
    const keywords = currentText.toLowerCase().match(/\b\w{4,}\b/g) || [];
    
    essays.forEach(essay => {
      if (essay.id === selectedDocument.id) return;
      
      const essayText = (essay.content || essay.body || "").toLowerCase();
      const commonKeywords = keywords.filter(keyword => 
        essayText.includes(keyword) && keyword.length > 4
      );
      
      if (commonKeywords.length > 2) {
        connections.push({
          document: essay,
          commonThemes: commonKeywords.slice(0, 3),
          type: 'essay'
        });
      }
    });

    return connections;
  };

  const strategicInsights = getStrategicInsights();
  const thematicConnections = getThematicConnections();

  return (
    <div className={`w-80 bg-stone-50 border-l border-stone-200 flex flex-col overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-stone-200 bg-white">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold text-slate-900">Writing Support</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>
        
        {/* Essay Slot Info - Only show if this is a slot essay */}
        {slotInfo && (
          <div className="mt-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
            <div className="flex items-start gap-2">
              <Target className="w-4 h-4 text-violet-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-violet-900 mb-1">
                  {slotInfo.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {slotInfo.collegeName && ` - ${slotInfo.collegeName}`}
                </h4>
                {slotInfo.prompt && (
                  <p className="text-xs text-violet-700 mb-2">
                    {slotInfo.prompt}
                  </p>
                )}
                <div className="flex items-center gap-3 text-xs text-violet-600">
                  {slotInfo.wordLimit && (
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      {slotInfo.wordLimit} words
                    </span>
                  )}
                  {slotInfo.isOptional && (
                    <span className="bg-violet-100 px-2 py-0.5 rounded-full">
                      Optional
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Tab Navigation */}
        <div className="flex gap-1 mt-3">
          <button 
            onClick={() => setActiveTab('freewrites')}
            className={`px-2 py-1.5 text-sm font-medium rounded transition-colors ${
              activeTab === 'freewrites' 
                ? 'bg-violet-100 text-violet-700' 
                : 'text-stone-600 hover:text-stone-800 hover:bg-stone-100'
            }`}
          >
            Freewrites
          </button>
          <button 
            onClick={() => setActiveTab('ideas')}
            className={`px-2 py-1.5 text-sm font-medium rounded transition-colors ${
              activeTab === 'ideas' 
                ? 'bg-violet-100 text-violet-700' 
                : 'text-stone-600 hover:text-stone-800 hover:bg-stone-100'
            }`}
          >
            Ideas
          </button>
          <button 
            onClick={() => setActiveTab('strategy')}
            className={`px-2 py-1.5 text-sm font-medium rounded transition-colors ${
              activeTab === 'strategy' 
                ? 'bg-violet-100 text-violet-700' 
                : 'text-stone-600 hover:text-stone-800 hover:bg-stone-100'
            }`}
          >
            Strategy
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'freewrites' && (
          <div className="p-4 space-y-4">
            {attachedFreewrites.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-azure-600" />
                  <h4 className="font-medium text-slate-900">Attached Freewrites</h4>
                  <span className="text-xs text-stone-500">({attachedFreewrites.length})</span>
                </div>
                {attachedFreewrites.map((freewrite) => (
                  <div key={freewrite.id} className="bg-white rounded-lg border border-stone-200">
                    <button
                      onClick={() => toggleItem(freewrite.id)}
                      className="w-full p-3 flex items-center justify-between hover:bg-stone-50 transition-colors"
                    >
                      <div className="flex items-start gap-2 flex-1 text-left">
                        <Sparkles className="w-4 h-4 text-violet-500 mt-0.5" />
                        <div className="flex-1">
                          <h5 className="font-medium text-sm text-slate-900">
                            {freewrite.title || freewrite.date || 'Untitled Freewrite'}
                          </h5>
                          {freewrite.prompt && (
                            <p className="text-xs text-stone-600 mt-1">
                              Prompt: {freewrite.prompt.substring(0, 60)}...
                            </p>
                          )}
                        </div>
                      </div>
                      {expandedItems[freewrite.id] ? 
                        <ChevronUp className="w-4 h-4 text-stone-400" /> : 
                        <ChevronDown className="w-4 h-4 text-stone-400" />
                      }
                    </button>
                    {expandedItems[freewrite.id] && (
                      <div className="p-3 pt-0 border-t border-stone-100">
                        <div className="text-sm text-stone-700 whitespace-pre-wrap max-h-96 overflow-y-auto">
                          {freewrite.content || freewrite.body || 'No content available'}
                        </div>
                        {freewrite.created_at && (
                          <p className="text-xs text-stone-500 mt-2">
                            Written: {new Date(freewrite.created_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                <p className="text-sm text-stone-500">No freewrites attached</p>
                <p className="text-xs text-stone-400 mt-1">Attach freewrites from the Essay Slot view</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'ideas' && (
          <div className="p-4 space-y-4">
            {attachedIdeas.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-yellow-600" />
                  <h4 className="font-medium text-slate-900">Attached Ideas</h4>
                  <span className="text-xs text-stone-500">({attachedIdeas.length})</span>
                </div>
                {attachedIdeas.map((idea) => (
                  <div key={idea.id} className="bg-white rounded-lg border border-stone-200">
                    <button
                      onClick={() => toggleItem(idea.id)}
                      className="w-full p-3 flex items-center justify-between hover:bg-stone-50 transition-colors"
                    >
                      <div className="flex items-start gap-2 flex-1 text-left">
                        <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5" />
                        <div className="flex-1">
                          <h5 className="font-medium text-sm text-slate-900">
                            {idea.title || 'Untitled Idea'}
                          </h5>
                          {idea.description && (
                            <p className="text-xs text-stone-600 mt-1">
                              {idea.description.substring(0, 80)}...
                            </p>
                          )}
                        </div>
                      </div>
                      {expandedItems[idea.id] ? 
                        <ChevronUp className="w-4 h-4 text-stone-400" /> : 
                        <ChevronDown className="w-4 h-4 text-stone-400" />
                      }
                    </button>
                    {expandedItems[idea.id] && (
                      <div className="p-3 pt-0 border-t border-stone-100">
                        <div className="text-sm text-stone-700 whitespace-pre-wrap max-h-96 overflow-y-auto">
                          <p className="font-medium mb-2">{idea.title}</p>
                          {idea.description && (
                            <p className="mb-2">{idea.description}</p>
                          )}
                          {idea.content && (
                            <div className="mt-2 pt-2 border-t border-stone-100">
                              {idea.content}
                            </div>
                          )}
                        </div>
                        {idea.tags && idea.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {idea.tags.map((tag, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Lightbulb className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                <p className="text-sm text-stone-500">No ideas attached</p>
                <p className="text-xs text-stone-400 mt-1">Attach ideas from the Essay Slot view</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'strategy' && (
          <div className="p-4 space-y-4">
            {collegeInsights ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-emerald-600" />
                  <h4 className="font-medium text-slate-900">College Strategy & Insights</h4>
                </div>
                
                {/* Strategic Advantages (from essay slots) */}
                {collegeInsights.strategic_advantages && (
                  <div className="bg-white rounded-lg p-3 border border-stone-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-4 h-4 text-violet-600" />
                      <h5 className="font-medium text-sm text-slate-900">Strategic Advantages</h5>
                    </div>
                    <p className="text-sm text-stone-700">
                      {collegeInsights.strategic_advantages}
                    </p>
                  </div>
                )}
                
                {/* Why This School (from essay slots) */}
                {collegeInsights.why_this_school && (
                  <div className="bg-white rounded-lg p-3 border border-stone-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-emerald-600" />
                      <h5 className="font-medium text-sm text-slate-900">Why This School</h5>
                    </div>
                    <p className="text-sm text-stone-700">
                      {collegeInsights.why_this_school}
                    </p>
                  </div>
                )}
                
                {/* Strategy Notes - Programs & Research */}
                {collegeInsights.strategy_notes?.programs?.value && (
                  <div className="bg-white rounded-lg p-3 border border-stone-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Building className="w-4 h-4 text-violet-600" />
                      <h5 className="font-medium text-sm text-slate-900">Programs & Research</h5>
                    </div>
                    <p className="text-sm text-stone-700">
                      {collegeInsights.strategy_notes.programs.value}
                    </p>
                  </div>
                )}
                
                {/* Strategy Notes - What Excites You */}
                {collegeInsights.strategy_notes?.interests?.value && (
                  <div className="bg-white rounded-lg p-3 border border-stone-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="w-4 h-4 text-red-500" />
                      <h5 className="font-medium text-sm text-slate-900">What Excites You</h5>
                    </div>
                    <p className="text-sm text-stone-700">
                      {collegeInsights.strategy_notes.interests.value}
                    </p>
                  </div>
                )}
                
                {/* Strategy Notes - Your Uniqueness */}
                {collegeInsights.strategy_notes?.perspectives?.value && (
                  <div className="bg-white rounded-lg p-3 border border-stone-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-violet-600" />
                      <h5 className="font-medium text-sm text-slate-900">Your Uniqueness</h5>
                    </div>
                    <p className="text-sm text-stone-700">
                      {collegeInsights.strategy_notes.perspectives.value}
                    </p>
                  </div>
                )}
                
                {/* Strategy Notes - Campus Life */}
                {collegeInsights.strategy_notes?.campus_life?.value && (
                  <div className="bg-white rounded-lg p-3 border border-stone-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-azure-600" />
                      <h5 className="font-medium text-sm text-slate-900">Campus Life</h5>
                    </div>
                    <p className="text-sm text-stone-700">
                      {collegeInsights.strategy_notes.campus_life.value}
                    </p>
                  </div>
                )}
                
                {/* Legacy format support - Strategic Positioning */}
                {collegeInsights.strategicPositioning && (
                  <div className="bg-white rounded-lg p-3 border border-stone-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-4 h-4 text-violet-600" />
                      <h5 className="font-medium text-sm text-slate-900">Strategic Positioning</h5>
                    </div>
                    <p className="text-sm text-stone-700">
                      {collegeInsights.strategicPositioning.brand || collegeInsights.strategicPositioning}
                    </p>
                  </div>
                )}

                {/* College-Specific Notes */}
                {collegeInsights.collegeNotes && (
                  <div className="bg-white rounded-lg p-3 border border-stone-200">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-4 h-4 text-azure-600" />
                      <h5 className="font-medium text-sm text-slate-900">College Notes</h5>
                    </div>
                    <p className="text-sm text-stone-700 whitespace-pre-wrap">
                      {collegeInsights.collegeNotes}
                    </p>
                  </div>
                )}

                {/* Why This College */}
                {collegeInsights.whyThisCollege && (
                  <div className="bg-white rounded-lg p-3 border border-stone-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-emerald-600" />
                      <h5 className="font-medium text-sm text-slate-900">Why This College</h5>
                    </div>
                    <ul className="text-sm text-stone-700 space-y-1">
                      {Array.isArray(collegeInsights.whyThisCollege) ? 
                        collegeInsights.whyThisCollege.map((reason, idx) => (
                          <li key={idx}>• {reason}</li>
                        )) : 
                        <li>{collegeInsights.whyThisCollege}</li>
                      }
                    </ul>
                  </div>
                )}

                {/* Key Themes */}
                {collegeInsights.keyThemes && (
                  <div className="bg-white rounded-lg p-3 border border-stone-200">
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="w-4 h-4 text-orange-600" />
                      <h5 className="font-medium text-sm text-slate-900">Key Themes</h5>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(Array.isArray(collegeInsights.keyThemes) ? collegeInsights.keyThemes : [collegeInsights.keyThemes]).map((theme, idx) => (
                        <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                          {theme}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Any additional insights */}
                {Object.keys(collegeInsights).length > 0 && (
                  <div className="text-xs text-stone-500 mt-4">
                    <p>Use these insights to align your essay with the college's values and priorities</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Target className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                <p className="text-sm text-stone-500">No strategy notes attached</p>
                <p className="text-xs text-stone-400 mt-1">Add college insights from the Essay Slot view</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WritingSupportSidebar;